import { useEffect, useMemo, useState } from 'react'
import { parseMoneyInput, rateOnDate, workspaceCurrency } from '../lib/billing'
import { useApp } from '../store/AppContext'
import { useFocusTrap } from '../lib/hooks'
import { clock, DAY_SEC, parseClock, todayKey } from '../lib/time'
import { api } from '../lib/api'
import {
  blockedByRules,
  liveOfKind,
  ROLE_BLURB,
  SWATCHES,
  type Client,
  type ClientKind,
  type Entry,
  type ID,
  type Member,
  type Project,
  type Role,
} from '../lib/types'
import { ClientPicker } from './ClientPicker'
import { ProjectPicker } from './ProjectPicker'
import { Field, SwatchPicker, Toggle } from './ui'

export function ModalHost() {
  const { modal } = useApp()
  if (!modal) return null
  return <ModalFrame key={modal.kind} />
}

function ModalFrame() {
  const { modal, setModal } = useApp()
  const trapRef = useFocusTrap<HTMLDivElement>(!!modal)
  if (!modal) return null

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && setModal(null)}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title" ref={trapRef}>
        {modal.kind === 'confirm' ? (
          <ConfirmBody />
        ) : modal.kind === 'project' ? (
          <ProjectForm project={modal.project} />
        ) : modal.kind === 'client' ? (
          <ClientForm client={modal.client} />
        ) : modal.kind === 'invite' ? (
          <InviteForm />
        ) : modal.kind === 'member' ? (
          <MemberForm member={modal.member} />
        ) : modal.kind === 'profile' ? (
          <ProfileForm />
        ) : (
          <EntryForm scope={modal.scope} entry={modal.entry} day={modal.day} />
        )}
      </div>
    </div>
  )
}

function Head({ title }: { title: string }) {
  const { setModal } = useApp()
  return (
    <div className="modal-head">
      <h2 id="modal-title">{title}</h2>
      <button type="button" className="ibtn" onClick={() => setModal(null)} aria-label="Close dialog">
        ✕
      </button>
    </div>
  )
}

function Foot({
  cta,
  onSave,
  danger,
  busy,
  disabled,
  submit,
}: {
  cta: string
  onSave: () => void
  danger?: boolean
  busy?: boolean
  /** Not ready yet -- distinct from busy, which means already under way. */
  disabled?: boolean
  /** True when this Foot sits inside a <form> that owns the saving. */
  submit?: boolean
}) {
  const { setModal } = useApp()
  return (
    <div className="modal-foot">
      <button type="button" className="btn" onClick={() => setModal(null)}>
        Cancel
      </button>
      {/* A real submit button, so Enter in any field saves the dialog --
          browsers only submit implicitly when a form contains one. onClick
          is dropped in that case, or the click would save twice. */}
      <button
        type={submit ? 'submit' : 'button'}
        className={danger ? 'btn btn--danger' : 'btn btn--primary'}
        style={{ padding: '10px 18px' }}
        onClick={submit ? undefined : onSave}
        disabled={busy || disabled}
      >
        {busy ? 'Saving…' : cta}
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ */

function ConfirmBody() {
  const { modal, setModal } = useApp()
  // Hooks cannot sit behind the guard below, so the state is declared first
  // and simply unused when this is not a confirm.
  const [typed, setTyped] = useState('')
  if (modal?.kind !== 'confirm') return null

  const need = modal.requireText
  // Trimmed and case-insensitive: the point is deliberation, not dictation.
  const ready = !need || typed.trim().toLowerCase() === need.trim().toLowerCase()

  return (
    <>
      <Head title={modal.title} />
      <div className="modal-body">
        <p className="pretty" style={{ margin: 0, color: 'var(--mut)', fontSize: 14, lineHeight: 1.55 }}>
          {modal.body}
        </p>

        {need && (
          <Field label={'Type ' + need + ' to confirm'}>
            <input
              autoFocus
              className="input input--canvas"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={need}
              aria-label={'Type ' + need + ' to confirm'}
              autoComplete="off"
            />
          </Field>
        )}
      </div>
      <Foot
        cta={modal.cta}
        danger={modal.danger}
        disabled={!ready}
        onSave={() => {
          if (!ready) return
          modal.onConfirm()
          setModal(null)
        }}
      />
    </>
  )
}

/* ------------------------------------------------------------------ */

function ProjectForm({ project }: { project?: Project }) {
  const { ws, act, setModal, busy } = useApp()
  const editing = !!project
  const [name, setName] = useState(project?.name ?? '')
  const [clientId, setClientId] = useState<ID | ''>(project?.clientId ?? '')
  const [color, setColor] = useState(project?.color ?? SWATCHES[0])
  const [rate, setRate] = useState(project ? String(project.rate) : '')
  const [estimate, setEstimate] = useState(project ? String(project.estimate || '') : '')
  const [tasks, setTasks] = useState(project?.tasks.join(', ') ?? '')
  const [billable, setBillable] = useState(project?.billable ?? true)
  const [err, setErr] = useState('')

  const save = async () => {
    if (!name.trim()) {
      setErr('Give the project a name.')
      return
    }
    const body = {
      name: name.trim(),
      clientId: clientId || null,
      color,
      billable,
      rate: Math.max(0, Number(rate) || 0),
      estimate: Math.max(0, Number(estimate) || 0),
      tasks: tasks.split(',').map((t) => t.trim()).filter(Boolean),
    }
    const ok = editing ? await act.updateProject(project.id, body) : await act.createProject(body)
    if (ok) setModal(null)
  }

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <Head title={editing ? 'Edit project' : 'New project'} />
      <div className="modal-body">
        <Field label="Project name">
          <input
            className="input input--canvas"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setErr('')
            }}
            placeholder="e.g. Spring Launch"
          />
        </Field>
        <Field label="Client">
          <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">No client</option>
            {/* Clients only. This modal is reached from the Projects screen,
                which is a client surface -- a prospect's projects are made
                from the Prospects screen's own picker. */}
            {liveOfKind(ws?.clients ?? [], 'client').map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Colour">
          <SwatchPicker value={color} onChange={setColor} />
        </Field>

        <div className="setting-row" style={{ borderBottom: 0, padding: 0 }}>
          <div style={{ flex: 1 }}>
            <h3>Billable by default</h3>
            <p>New time on this project is marked billable. Any single entry can still be flipped.</p>
          </div>
          <Toggle checked={billable} onChange={setBillable} label="Billable by default" />
        </div>

        <Field
          label="Hourly rate ($)"
          hint="Turns billable hours into an amount in reports and the CSV export. 0 if you don't bill by the hour."
        >
          <input
            className="input input--canvas"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="140"
          />
        </Field>
        <Field label="Time estimate (hours)" hint="Drives the budget bar on the project card. 0 for no budget.">
          <input
            className="input input--canvas"
            inputMode="decimal"
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
            placeholder="80"
          />
        </Field>
        <Field label="Tasks" hint="Comma separated. Tasks show under the project in the timer's picker.">
          <input
            className="input input--canvas"
            value={tasks}
            onChange={(e) => setTasks(e.target.value)}
            placeholder="Discovery, Wireframes, Handoff"
          />
        </Field>
        {err && <div className="auth-err">{err}</div>}
      </div>
      <Foot cta={editing ? 'Save changes' : 'Create project'} submit onSave={() => void save()} busy={busy} />
    </form>
  )
}

/* ------------------------------------------------------------------ */

function ClientForm({ client }: { client?: Client }) {
  const { act, setModal, busy } = useApp()
  const editing = !!client

  /*
   * This form is opened from both screens now, so it says which of the two
   * it is editing. Taken from the row itself rather than from a prop: the
   * modal is told the client, and the client already knows its kind.
   *
   * It does not offer to change the kind. A prospect that becomes a client
   * is a real event worth a deliberate action, not something to happen by
   * mis-clicking a dropdown while fixing a typo in a name.
   */
  const noun = client?.isProspect ? 'prospect' : 'client'
  const Noun = client?.isProspect ? 'Prospect' : 'Client'
  const [name, setName] = useState(client?.name ?? '')
  const [contact, setContact] = useState(client?.contact === '—' ? '' : (client?.contact ?? ''))
  const [err, setErr] = useState('')

  const save = async () => {
    if (!name.trim()) {
      setErr(`Give the ${noun} a name.`)
      return
    }
    const body = { name: name.trim(), contact: contact.trim() }
    const ok = editing ? await act.updateClient(client.id, body) : await act.createClient(body)
    if (ok) setModal(null)
  }

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <Head title={`${editing ? 'Edit' : 'New'} ${noun}`} />
      <div className="modal-body">
        <Field label={`${Noun} name`}>
          <input
            className="input input--canvas"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setErr('')
            }}
            placeholder="e.g. Meridian Bank"
          />
        </Field>
        <Field label="Billing contact">
          <input
            className="input input--canvas"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="accounts@meridian.com"
          />
        </Field>
        {err && <div className="auth-err">{err}</div>}
      </div>
      <Foot cta={editing ? 'Save changes' : 'Add client'} submit onSave={() => void save()} busy={busy} />
    </form>
  )
}

/* ------------------------------------------------------------------ */

function InviteForm() {
  const { act, setModal, busy, flash } = useApp()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [capacity, setCapacity] = useState('40')
  // Kept on screen after a successful invite rather than closing the dialog:
  // the link is the part that reliably works, so it must not vanish before
  // it has been copied.
  const [link, setLink] = useState<string | null>(null)
  const [linkProblem, setLinkProblem] = useState<string | null>(null)
  const [added, setAdded] = useState(false)
  const [emailed, setEmailed] = useState(false)
  const [emailProblem, setEmailProblem] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const save = async () => {
    const result = await act.invite({
      email: email.trim().toLowerCase(),
      role,
      capacity: Math.max(1, Number(capacity) || 40),
    })
    if (!result.ok) return

    // Always stay open on success. Closing when there was no link is what
    // turned a link-generation failure into a dialog that just vanished.
    setEmailed(result.emailed)
    setEmailProblem(result.emailProblem)
    setLink(result.actionLink)
    setLinkProblem(result.linkProblem)
    setAdded(true)
  }

  const copy = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      flash('Copy failed — select the link and copy it manually.')
    }
  }

  if (added) {
    return (
      <>
        <Head title={`${email} added`} />
        <div className="modal-body">
          <p className="pretty" style={{ fontSize: 13, margin: '0 0 12px' }}>
            {emailed
              ? 'They have been emailed a link to set their password. Email is easily filtered, so here is the same link to send them directly if it does not arrive.'
              : 'They have been added, but the email did not go out — send them this link to set their password.'}
          </p>

          {/* The mail server's own words. Without them "it did not go out"
              is a dead end for whoever has to fix the sending. */}
          {!emailed && emailProblem && (
            <p
              className="pretty"
              style={{ fontSize: 12.5, color: 'var(--dg)', margin: '-4px 0 12px' }}
            >
              The mail server said: {emailProblem}
            </p>
          )}

          {link ? (
            <>
              <textarea
                readOnly
                className="input input--canvas"
                style={{ width: '100%', minHeight: 84, fontSize: 12, fontFamily: 'var(--mono, monospace)' }}
                value={link}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="Sign-in link"
              />
              <p style={{ fontSize: 12, color: 'var(--mut)', margin: '8px 0 0' }}>
                One use only, and it expires. Generate a fresh one by inviting them again.
              </p>
            </>
          ) : (
            <p style={{ fontSize: 12.5, color: 'var(--dg)', margin: 0 }}>
              They are in the workspace, but no sign-in link could be made
              {linkProblem ? `: ${linkProblem}` : '.'} They can still use
              “Forgot your password?” on the sign-in screen to set one.
            </p>
          )}
        </div>
        <div className="modal-foot">
          <button type="button" className="btn" onClick={() => setModal(null)}>
            Done
          </button>
          {link && (
            <button type="button" className="btn btn--primary" onClick={() => void copy()}>
              {copied ? '✓ Copied' : 'Copy link'}
            </button>
          )}
        </div>
      </>
    )
  }

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <Head title="Invite to workspace" />
      <div className="modal-body">
        <Field
          label="Email"
          hint="They are added straight away, and you get a link to send them. An email goes out too, but email is easily filtered so the link is the reliable part."
        >
          <input
            className="input input--canvas"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@studio.com"
          />
        </Field>
        <Field label="Role">
          <select className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {(['member', 'manager', 'admin'] as const).map((r) => (
              <option key={r} value={r}>
                {ROLE_BLURB[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Weekly capacity (hours)" hint="Used for the utilisation bar on the Team screen.">
          <input
            className="input input--canvas"
            inputMode="numeric"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder="40"
          />
        </Field>
      </div>
      <Foot cta="Create invite" submit onSave={() => void save()} busy={busy} />
    </form>
  )
}

function MemberForm({ member }: { member: Member }) {
  const { act, setModal, busy } = useApp()
  const [capacity, setCapacity] = useState(String(member.capacity))

  const save = async () => {
    const ok = await act.updateMember(member.id, { capacity: Math.max(1, Number(capacity) || 40) })
    if (ok) setModal(null)
  }

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <Head title={`${member.name}'s capacity`} />
      <div className="modal-body">
        <Field label="Name">
          <input className="input input--canvas" value={member.name} readOnly disabled />
        </Field>
        <Field label="Email">
          <input className="input input--canvas" value={member.email} readOnly disabled />
        </Field>
        <Field
          label="Weekly capacity (hours)"
          hint="People manage their own name and password from their account — you set how many hours a full week is for them."
        >
          <input
            className="input input--canvas"
            inputMode="numeric"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
          />
        </Field>
      </div>
      <Foot cta="Save changes" submit onSave={() => void save()} busy={busy} />
    </form>
  )
}

/* ------------------------------------------------------------------ */

function ProfileForm() {
  const { user, setModal, refreshAuth, flash } = useApp()
  const [name, setName] = useState(user?.name ?? '')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!name.trim()) {
      setErr('Enter your name.')
      return
    }
    if (next && user?.hasPassword && !current) {
      setErr('Enter your current password to change it.')
      return
    }
    setSaving(true)
    try {
      await api.updateProfile({
        name: name.trim(),
        ...(next ? { password: next, currentPassword: current } : {}),
      })
      await refreshAuth()
      flash(next ? 'Profile and password updated' : 'Profile updated')
      setModal(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <Head title="Your account" />
      <div className="modal-body">
        <Field label="Name" hint="This is how you appear to everyone in your workspaces.">
          <input
            className="input input--canvas"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setErr('')
            }}
          />
        </Field>
        <Field label="Email">
          <input className="input input--canvas" value={user?.email ?? ''} readOnly disabled />
        </Field>
        {user?.viaGoogle && (
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--mut)',
              background: 'var(--canvas)',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '10px 12px',
            }}
            className="pretty"
          >
            You sign in with Google.{' '}
            {user.hasPassword
              ? 'You can also use the password below.'
              : 'Setting a password here is optional — it gives you a way in if Google is ever unavailable.'}
          </div>
        )}

        {user?.hasPassword && (
          <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
            <Field label="Current password" hint="Only needed if you're setting a new one.">
              <input
                className="input input--canvas"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => {
                  setCurrent(e.target.value)
                  setErr('')
                }}
              />
            </Field>
          </div>
        )}
        <Field
          label={user?.hasPassword ? 'New password' : 'Set a password'}
          hint="At least 8 characters. Changing it signs you out everywhere else."
        >
          <input
            className="input input--canvas"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => {
              setNext(e.target.value)
              setErr('')
            }}
          />
        </Field>
        {err && <div className="auth-err">{err}</div>}
      </div>
      <Foot cta="Save" submit onSave={() => void save()} busy={saving} />
    </form>
  )
}

/* ------------------------------------------------------------------ */

function EntryForm({
  scope,
  entry,
  day,
}: {
  /** Clients or prospects: whichever screen this was opened from. */
  scope: ClientKind
  entry?: Entry
  day?: string
}) {
  const { ws, me, canSeeAll, act, setModal, proj, busy } = useApp()
  const editing = !!entry
  const fmt = ws?.settings.timeFormat ?? '12h'

  const [desc, setDesc] = useState(entry?.desc ?? '')
  const [userId, setUserId] = useState<ID>(entry?.userId ?? me?.id ?? '')
  const [entryDay, setEntryDay] = useState(entry?.day ?? day ?? todayKey())
  const [projectId, setProjectId] = useState<ID | ''>(entry?.projectId ?? '')
  /*
   * A client is not stored on an entry -- it is reached through the project.
   * It is held here so this form can offer the same two-step choice the timer
   * bar does: narrow by client, or pick a project and let the client fill
   * itself in. Without it "Require a client" was a rule the form gave you no
   * way to satisfy.
   */
  const [clientId, setClientId] = useState<ID | null>(
    entry?.projectId ? (proj(entry.projectId)?.clientId ?? null) : null,
  )
  const [task, setTask] = useState(entry?.task ?? '')
  // Seconds are shown so an edit cannot silently truncate a timed entry to
  // the minute; parseClock accepts them with or without.
  const [start, setStart] = useState(entry ? clock(entry.start, fmt) : '9:00 AM')
  const [end, setEnd] = useState(entry ? clock(entry.start + entry.dur, fmt) : '10:30 AM')
  const [tags, setTags] = useState<string[]>(entry?.tags ?? [])
  const [billable, setBillable] = useState(entry?.billable ?? true)
  /*
   * Pricing for this one entry. A flat fee is a toggle because it is a
   * different KIND of charge, not a bigger number: when it is on, the
   * duration stops deciding the money and the hourly rate has nothing to
   * multiply, so the rate field goes away rather than sitting there inert.
   */
  const [fixedOn, setFixedOn] = useState((entry?.fixedAmount ?? null) !== null)
  const [fixedAmount, setFixedAmount] = useState(
    entry?.fixedAmount != null ? String(entry.fixedAmount) : '',
  )
  const [rateOverride, setRateOverride] = useState(
    entry?.rateOverride != null ? String(entry.rateOverride) : '',
  )
  const [err, setErr] = useState('')

  /**
   * The currency this entry will actually be priced in.
   *
   * It used to be workspaceCurrency(ws.rates), which returns the FIRST rate in
   * the array -- an arbitrary row, and in a workspace holding more than one
   * currency it labelled the box with whichever happened to sort first. The
   * answer is knowable: the rate in force for the person this entry belongs
   * to, on the day it is dated.
   *
   * There is still a fallback for somebody with no rate yet, and that is now
   * USD. Showing a currency the entry will not be billed in is worse than
   * showing none, so this follows the rate rather than a preference.
   */
  const currency = useMemo(() => {
    const theirs = (ws?.rates ?? [])
      .filter((r) => r.userId === userId)
      .map((r) => ({
        hourly_rate: r.hourlyRate,
        currency: r.currency,
        effective_from: r.effectiveFrom,
      }))
    return rateOnDate(theirs, entryDay)?.currency ?? workspaceCurrency(ws?.rates ?? [])
  }, [ws?.rates, userId, entryDay])

  const project = proj(projectId || null)
  useEffect(() => {
    if (task && !project?.tasks.includes(task)) setTask('')
  }, [projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  const a = parseClock(start)
  const b = parseClock(end)
  const dur = a !== null && b !== null ? (b - a + DAY_SEC) % DAY_SEC : null

  const save = async () => {
    if (a === null || b === null) {
      setErr('Use times like 9:00, 9:30 am or 17:45.')
      return
    }
    if (!dur) {
      setErr('Start and end are the same — that would log zero time.')
      return
    }
    if (billable && fixedOn && parseMoneyInput(fixedAmount) === null) {
      setErr('Enter the fixed fee, or switch it off to charge by the hour.')
      return
    }

    /*
     * The workspace rules, checked here as well as on the timer.
     *
     * blockedByRules has guarded the Start button since it was written, but
     * this form never called it. So "Require a project" stopped a timer and
     * waved through the very same hour typed into this box -- a rule with a
     * door left open beside it is not a rule, and the entries that came
     * through it charge at the person's rate with no client to invoice.
     *
     * It applies to edits too. An entry already saved without a project has
     * to gain one before it can be changed, which is how the ones logged
     * before the rule was turned on get cleaned up rather than quietly kept.
     */
    const blocked = ws ? blockedByRules(ws.settings, project) : null
    if (blocked) {
      setErr(blocked)
      return
    }

    const body = {
      desc: desc.trim(),
      userId,
      day: entryDay,
      projectId: projectId || null,
      task,
      tags,
      billable,
      start: a,
      dur,
      /*
       * Sent by everyone now. guard_entry_pricing used to refuse these from a
       * member -- even a null -- so they had to be left out entirely or an
       * ordinary edit would be rejected as a pricing change. That guard was
       * dropped in 20261013100000; RLS still decides WHOSE entry this is, so a
       * member can only ever price their own.
       */
      fixedAmount: fixedOn ? parseMoneyInput(fixedAmount) : null,
      rateOverride: fixedOn ? null : parseMoneyInput(rateOverride),
    }
    const ok = editing ? await act.updateEntry(entry.id, body) : await act.createEntry(body)
    if (ok) setModal(null)
  }

  // Only real members can own an entry; pending invites have no account yet.
  const assignable = (ws?.team ?? []).filter((m) => m.status === 'active')

  return (
    <form onSubmit={(e) => {
      e.preventDefault()
      void save()
    }}>
      <Head title={editing ? 'Edit entry' : 'Add time entry'} />
      <div className="modal-body">
        <Field label="Description">
          <input
            className="input input--canvas"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What did you work on?"
          />
        </Field>

        {canSeeAll && assignable.length > 1 && (
          <Field label="Member" hint="Owners and managers can log or correct time on someone else's behalf.">
            <select className="select" value={userId} onChange={(e) => setUserId(e.target.value)}>
              {assignable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.id === me?.id ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="Date">
          <input
            className="input input--canvas"
            type="date"
            value={entryDay}
            onChange={(e) => e.target.value && setEntryDay(e.target.value)}
          />
        </Field>

{/* The same two controls as the timer bar, so the choice is made the same
            way in both places. The project picker carries tasks in its own list,
            which is why the separate task select is gone. */}
        <Field label={scope === 'prospect' ? 'Prospect and project' : 'Client and project'}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <ClientPicker
              kind={scope}
              clientId={clientId}
              align="left"
              onPick={(c) => {
                setClientId(c)
                setErr('')
                // A project belongs to one client, so a project from another
                // is no longer a valid answer. Cleared rather than left
                // contradicting the client beside it.
                if (c && project && project.clientId !== c) {
                  setProjectId('')
                  setTask('')
                }
              }}
            />
            <ProjectPicker
              kind={scope}
              projectId={projectId || null}
              task={task}
              clientId={clientId}
              align="left"
              onPick={(pid, t) => {
                setProjectId(pid ?? '')
                setTask(t)
                setErr('')
                const p = pid ? ws?.projects.find((x) => x.id === pid) : null
                if (p) {
                  setBillable(p.billable)
                  setClientId(p.clientId ?? null)
                }
              }}
            />
          </div>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <Field label="Start">
            <input
              className="input input--canvas"
              value={start}
              onChange={(e) => {
                setStart(e.target.value)
                setErr('')
              }}
              placeholder="9:00 AM"
            />
          </Field>
          <Field label="End">
            <input
              className="input input--canvas"
              value={end}
              onChange={(e) => {
                setEnd(e.target.value)
                setErr('')
              }}
              placeholder="10:30 AM"
            />
          </Field>
          <div style={{ paddingBottom: 11, fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--mut)' }}>
            {dur === null
              ? '—'
              : `${Math.floor(dur / 3600)}:${String(Math.round((dur % 3600) / 60)).padStart(2, '0')}`}
          </div>
        </div>

        <Field label="Tags">
          {ws?.tags.length ? (
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {ws.tags.map((t) => {
                const on = tags.includes(t)
                return (
                  <button
                    key={t}
                    type="button"
                    className="chip chip--ac"
                    style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12.5 }}
                    aria-pressed={on}
                    onClick={() => setTags((cur) => (on ? cur.filter((x) => x !== t) : [...cur, t]))}
                  >
                    {t}
                  </button>
                )
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>No tags in this workspace yet.</div>
          )}
        </Field>

        <div className="setting-row" style={{ borderBottom: 0, paddingBottom: 0 }}>
          <div style={{ flex: 1 }}>
            <h3>Billable</h3>
            <p>Counts towards the amount in reports, at the project's rate.</p>
          </div>
          <Toggle checked={billable} onChange={setBillable} label="Billable" />
        </div>

        {billable && (
          <>
            <div className="setting-row" style={{ borderBottom: 0, paddingBottom: 0 }}>
              <div style={{ flex: 1 }}>
                <h3>Fixed fee</h3>
                <p>
                  Charge one amount for this entry, however long it took. Overrides every rate,
                  including the one below.
                </p>
              </div>
              <Toggle checked={fixedOn} onChange={setFixedOn} label="Fixed fee" />
            </div>

            {fixedOn ? (
              <Field
                label={`Fixed fee (${currency})`}
                hint="This exact amount appears in reports and in the Amount column of both exports. The duration is still recorded, it just stops deciding the money."
              >
                <input
                  className="input input--canvas"
                  inputMode="decimal"
                  value={fixedAmount}
                  onChange={(e) => setFixedAmount(e.target.value)}
                  placeholder="500"
                  aria-label="Fixed fee"
                />
              </Field>
            ) : (
              <Field
                label={`Rate for this entry (${currency} / h)`}
                hint="Use when somebody's rate changed after this work was logged. Leave blank to price it the usual way, from their charge-out rate or the project's."
              >
                <input
                  className="input input--canvas"
                  inputMode="decimal"
                  value={rateOverride}
                  onChange={(e) => setRateOverride(e.target.value)}
                  placeholder="Leave blank for the usual rate"
                  aria-label="Rate for this entry"
                />
              </Field>
            )}
          </>
        )}

      </div>
      {/* Outside the scrolling body on purpose. In it, the message sat at the
          end of a form taller than the window: you pressed a button that is
          always visible and were answered somewhere you could not see. Here it
          sits against the footer, so the answer arrives where the question was
          asked. */}
      {err && <div className="modal-err">{err}</div>}
      <Foot cta={editing ? 'Save changes' : 'Add entry'} submit onSave={() => void save()} busy={busy} />
    </form>
  )
}
